import React, { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, signOut } from 'firebase/auth';
import { doc, updateDoc, arrayUnion, increment, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase-config';

const FLAG_THRESHOLD = 3; // flag account after this many IP changes

export default function IPVerificationModal({ user, newIP, currentIPChangeCount, onVerified, onDismiss }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleVerify(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      // Reauth succeeded — update Firestore with new IP
      const ref = doc(db, 'users', user.uid);
      const userAgent = navigator.userAgent.slice(0, 150);
      const newChangeCount = (currentIPChangeCount || 0) + 1;

      await updateDoc(ref, {
        currentIP: newIP,
        ipChangeCount: increment(1),
        ipHistory: arrayUnion({
          ip: newIP,
          ua: userAgent,
          ts: Date.now(),
        }),
        // Auto-flag if hitting threshold (admin will see it)
        ...(newChangeCount >= FLAG_THRESHOLD ? { flagged: true } : {}),
      });

      onVerified(newIP);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. If this isn\'t you, someone may have your login — change your password immediately.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Try again in a few minutes.');
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    sessionStorage.setItem('om_intentional_signout', '1');
    await signOut(auth);
    if (onDismiss) onDismiss();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        backgroundColor: '#0f172a',
        border: '1px solid #334155',
        borderRadius: '12px',
        padding: '36px',
        maxWidth: '440px',
        width: '100%',
        boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{ fontSize: '28px' }}>🔐</span>
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '18px' }}>
              New Location Detected
            </div>
            <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '2px' }}>
              Security verification required
            </div>
          </div>
        </div>

        {/* Info box */}
        <div style={{
          backgroundColor: '#1e293b',
          border: '1px solid #f59e0b44',
          borderRadius: '8px',
          padding: '14px 16px',
          marginBottom: '24px',
        }}>
          <p style={{ color: '#fbbf24', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
            We noticed you're signing in from a <strong>new IP address</strong> ({newIP}).
            This could mean someone else is using your account. Please confirm it's you.
          </p>
          <p style={{ color: '#94a3b8', fontSize: '12px', margin: '8px 0 0', lineHeight: '1.5' }}>
            Signing in as <strong style={{ color: '#e2e8f0' }}>{user.email}</strong>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '13px', marginBottom: '6px' }}>
              Confirm your password to continue
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your password"
              required
              autoFocus
              style={{
                width: '100%',
                padding: '10px 14px',
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '6px',
                color: '#f1f5f9',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {error && (
            <div style={{
              color: '#f87171',
              fontSize: '13px',
              backgroundColor: '#450a0a44',
              border: '1px solid #f8717144',
              borderRadius: '6px',
              padding: '10px 14px',
              lineHeight: '1.5',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !password}
            style={{
              padding: '11px',
              backgroundColor: loading ? '#1d4ed8' : '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: !password ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
          >
            {loading ? 'Verifying...' : 'Confirm — It\'s Me'}
          </button>
        </form>

        {/* Sign out option */}
        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #1e293b', textAlign: 'center' }}>
          <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 8px' }}>
            Not you? Someone may have your credentials.
          </p>
          <button
            onClick={handleSignOut}
            style={{
              background: 'none', border: 'none',
              color: '#ef4444', fontSize: '13px',
              cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            Sign out &amp; change my password
          </button>
        </div>
      </div>
    </div>
  );
}
